import {
  CartItem,
  CartItemLegacyGroupEnum,
  CartItemTypeEnum,
} from '@sola/schema/dotcom/cart/cart-item.ts';
import { CartItemFund } from '@sola/schema/dotcom/cart/fund.ts';
import { CartItemGift } from '@sola/schema/dotcom/cart/gift.ts';
import { SponsorshipItemTypeEnum } from '@sola/schema/dotcom/cart/sponsorship.ts';
import { AnyObject } from '@sola/schema/dotcom/helpers.ts';

declare global {
  interface Window {
    dataLayer: AnyObject[];
  }
}

type AnalyticsCartItemData = {
  category?: string;
  name?: string;
  group?: CartItemLegacyGroupEnum;
};

export type AnalyticsCartItem = {
  item_id: string;
  item_gid: string;
  item_category: string;
  item_category2: string;
  item_name: string;
  item_variant: string;
  price: number;
  quantity: number;
};

/**
 *  Get the cart item fund name for analytics events.
 * @param fundType The type of the fund.
 * @param fundName The name of the fund.
 */
const getFundName = (fundType: string, fundName: string): string => {
  switch (fundType) {
    case 'DS':
      return 'Child Sponsorship';
    case '12MD':
    case '12SB':
    case '12NU':
    case '12BB':
    case '12BN':
    case '12NC':
      return 'Babies and Moms Fund';
    case '12GW':
      return 'Bibles Fund';
    case '12DR':
    case '12SL':
    case '12DW':
    case '12BR':
    case '12HD':
    case '12OC':
    case '12FS':
    case '12EC':
      return 'Children at Risk Fund';
    case '12ED':
    case '12VT':
    case '12AR':
    case '12BA':
    case '12BE':
    case '12ET':
    case '12CM':
    case '12LE':
    case '12MC':
    case '12PG':
    case '12SE':
    case '12SP':
    case '12ST':
    case '12TB':
      return 'College & Training Fund';
    case 'CF':
      return 'Christmas Gift Fund';
    case 'CP':
      return 'Critical Program Needs Fund';
    case 'ER':
      return 'Disaster Relief Fund';
    case 'ED':
      return 'Educational Needs Fund';
    case 'GFC':
      return 'Global Food Crisis Fund';
    case 'NH':
      return 'Health and Nutrition Fund';
    case '12DC':
    case '12EF':
    case '12BH':
    case '12TR':
    case '12HK':
    case '12SU':
    case '12MV':
    case '12PA':
    case '12IL':
    case '12VA':
      return 'Health Fund';
    case 'OV':
      return 'Highly Vulnerable Children Fund';
    case 'HA':
      return 'HIV and AIDS Initiative Fund';
    case '12GF':
    case '12FB':
    case '12FP':
      return 'Hunger Fund';
    case 'IG':
    case '12BI':
    case '12LV':
    case '12CK':
    case '12CW':
    case '12GS':
    case '12GO':
    case '12PI':
    case '12BL':
      return 'Income Generation Fund';
    case 'DI':
      return 'Infrastructure Development Fund';
    case 'MP':
      return 'Lifesaving Surgery Fund';
    case 'MV':
      return 'Malaria Intervention Fund';
    case 'MA':
      return 'Medical Assistance Fund';
    case 'MX':
      return 'Ministry Expansion Fund';
    case 'FF':
      return 'Partners of Compassion Fund';
    case 'WP':
      return 'Safe Water Sanitation Hygiene Fund';
    case '5S':
      return 'Survival Program Fund';
    case 'UC':
    case '12UC':
      return 'Unsponsored Children Fund';
    case '12WB':
    case '12EL':
    case '12BW':
    case '12WF':
    case '12WI':
      return 'Water and Energy Fund';
    case 'MN':
    case '12MS':
      return 'Where Most Needed Fund';
    case '5Y':
      return 'Youth Development Fund';
    default:
      return `${fundName} Fund`;
  }
};

export const mapSponsorshipTypeToLegacyGroup = (
  sponsorshipType?: SponsorshipItemTypeEnum,
): CartItemLegacyGroupEnum => {
  switch (sponsorshipType) {
    case SponsorshipItemTypeEnum.Selected:
      return CartItemLegacyGroupEnum.ChildSelected;
    case SponsorshipItemTypeEnum.EPayment:
      return CartItemLegacyGroupEnum.EPayment;
    case SponsorshipItemTypeEnum.FirstPayment:
      return CartItemLegacyGroupEnum.FirstPayment;
    case SponsorshipItemTypeEnum.LongestWaiting:
      return CartItemLegacyGroupEnum.LongestWaiting;
    case SponsorshipItemTypeEnum.ChildCriteria:
      return CartItemLegacyGroupEnum.ChildCriteria;
  }

  return CartItemLegacyGroupEnum.ChildSelected;
};

/**
 * Maps the cart item type to a cart item group.
 * @param item The cart item data.
 */
export const mapCartItemTypeToLegacyGroup = (
  item: Partial<CartItem>,
): CartItemLegacyGroupEnum => {
  switch (item.itemType) {
    case CartItemTypeEnum.Sponsorship:
      return mapSponsorshipTypeToLegacyGroup(
        item?.sponsorship?.sponsorshipType,
      );
    case CartItemTypeEnum.Payment:
      return CartItemLegacyGroupEnum.CommitmentPayment;
    case CartItemTypeEnum.Gift:
      return CartItemLegacyGroupEnum.GiftPayment;
    case CartItemTypeEnum.Fund:
      return CartItemLegacyGroupEnum.Fund;
  }

  return CartItemLegacyGroupEnum.Fund;
};

/**
 * Format cart type, name, and category for usage in analytics events
 * @param item The cart item data.
 */
const mapToAnalyticsData = (item: Partial<CartItem>): AnalyticsCartItemData => {
  const group = mapCartItemTypeToLegacyGroup(item);

  const getData = (): AnalyticsCartItemData | undefined => {
    switch (group) {
      case CartItemLegacyGroupEnum.ChildSelected:
        return {
          name: item.title,
          category: item.child?.country || '',
        };
      case CartItemLegacyGroupEnum.EPayment:
        return {
          name: 'E Payment',
        };
      case CartItemLegacyGroupEnum.FirstPayment:
        return {
          name: 'First Payment',
        };
      case CartItemLegacyGroupEnum.LongestWaiting:
        return {
          name: 'Longest Waiting Child',
        };
      case CartItemLegacyGroupEnum.ChildCriteria:
        return {
          name: 'Child Matching Certain Criteria',
        };
      case CartItemLegacyGroupEnum.CommitmentPayment:
        return {
          // TODO: This seems like a bug in legacy - not all "CommitmentPayment"s are sponsorships...
          name: 'Sponsorship Payment',
        };
      case CartItemLegacyGroupEnum.GiftPayment:
        return {
          name: `${(item as CartItemGift).gift?.giftType || ''} Gift`,
          category: (item as CartItemGift).gift?.giftType || '',
        };
      case CartItemLegacyGroupEnum.Fund:
        return {
          name: item.title,
          category: getFundName(
            (item as CartItemFund).fund?.fundCode || '',
            item.title!,
          ),
        };
    }
  };

  return {
    group,
    ...getData(),
  };
};

/**
 * Format cart items for analytics events.
 * @param items	Array of items to format.
 * @returns {array}
 */
export const mapAnalyticsCartItems = (
  items: Partial<CartItem>[],
): AnalyticsCartItem[] => {
  if (!items?.length) {
    return [];
  }

  return items.map((item): AnalyticsCartItem => {
    const { name, category, group } = mapToAnalyticsData(item);

    return {
      item_id: item.itemCode!,
      // TODO: Should we make item_gid optional?
      item_gid: item.child?.globalId || '',
      item_category: group || '',
      item_category2: category || '',
      item_name: name || item.title!,
      item_variant: item.commitmentFrequency || '',
      price: item.unitPrice!,
      quantity: item.quantity || 1,
    };
  });
};

/**
 * Puts an analytics event in the data layer.
 * @param eventName The event name.
 * @param data The data to add to the event.
 */
export const sendAnalyticsEvent = (
  eventName: string,
  data?: AnyObject,
): void => {
  // Define dataLayer so we can still queue up events before GTM init
  window.dataLayer = window.dataLayer || [];

  if (!data) {
    window.dataLayer.push({ event: eventName });
    return;
  }

  window.dataLayer.push({
    event: eventName,
    ...data,
    engagement_time_msec:
      data.engagement_time_msec ?? parseInt(performance.now().toString(), 10),
  });
};
